import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTheme } from '@mui/material/styles';
import { FieldProps } from 'formik';

type ReCaptchaCallbacks = {
  onChange: (token: string | null) => void;
  onErrored: () => void;
  onExpired: () => void;
};

export type ReCaptchaPropTypes = {
  googleRecaptchaKey: string;
  theme?: 'light' | 'dark';
  autoTheme?: boolean; // If true, use color mode and update dynamically
  fieldName?: string; // Field name for Formik integration (defaults to 'recaptcha')
} & (
  | (FieldProps & { onChange?: never; onErrored?: never; onExpired?: never }) // Formik Field component usage
  | (ReCaptchaCallbacks & { form?: never; field?: never; meta?: never }) // Manual callback usage
) &
  Omit<
    React.ComponentProps<typeof ReCAPTCHA>,
    'sitekey' | 'onChange' | 'onErrored' | 'onExpired' | 'theme'
  >;

const ReCaptcha = (props: ReCaptchaPropTypes) => {
  const {
    googleRecaptchaKey,
    theme,
    autoTheme = false,
    fieldName = 'recaptcha',
    onChange: onChangeCallback,
    onErrored: onErroredCallback,
    onExpired: onExpiredCallback,
    form,
    ...rest
  } = props;

  const muiTheme = useTheme();

  // Determine the current theme - update dynamically when autoTheme is true
  // Use MUI theme for autoTheme to match FormikReCaptcha behavior
  const currentTheme: 'light' | 'dark' =
    theme || (autoTheme ? muiTheme.palette.mode : 'light');

  // Determine if this is being used as a Formik Field component
  const isFormikField = !!form;

  const onChange = (response: string | null) => {
    if (isFormikField && form) {
      form.setFieldValue(fieldName, response, true);
      form.setFieldTouched(fieldName, true);
    } else if (onChangeCallback) {
      onChangeCallback(response);
    }
  };

  const onErrored = () => {
    if (isFormikField && form) {
      form.setFieldValue(fieldName, '', true);
      form.setFieldTouched(fieldName, true);
    } else if (onErroredCallback) {
      onErroredCallback();
    }
  };

  const onExpired = () => {
    if (isFormikField && form) {
      form.setFieldValue(fieldName, '', true);
      form.setFieldTouched(fieldName, true);
    } else if (onExpiredCallback) {
      onExpiredCallback();
    }
  };

  return (
    <ReCAPTCHA
      key={currentTheme}
      sitekey={googleRecaptchaKey}
      onChange={onChange}
      onErrored={onErrored}
      onExpired={onExpired}
      theme={currentTheme}
      {...rest}
    />
  );
};

export default ReCaptcha;
